import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  Camera, Upload, X, Stethoscope, Bone, Sparkles, Siren, Check,
  AlertTriangle, HelpCircle, Trash2, ChevronDown, PawPrint,
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { fileToBase64, timeAgo } from "@/lib/files";
import { Spinner } from "@/components/kit";
import UpgradeModal from "@/components/UpgradeModal";
import BreedResultCard from "@/components/BreedResultCard";
import DogAvatar from "@/components/DogAvatar";

const MODES = {
  symptom: { label: "Symptom", icon: Stethoscope, prompt: "Point your camera at a skin issue, wound, eye, ear or paw." },
  food: { label: "Is this safe?", icon: Bone, prompt: "Snap a food, treat or ingredient label to check if it's dog-safe." },
  breed: { label: "Identify breed", icon: PawPrint, prompt: "Snap a clear photo of your dog to guess its breed — great for mixes." },
};

const STATUS = {
  emergency: { bg: "bg-[#FDECEB]", border: "border-[#D9534F]", text: "text-[#D9534F]", icon: Siren },
  caution: { bg: "bg-[#FBEED9]", border: "border-[#F0D9A8]", text: "text-[#B7791F]", icon: AlertTriangle },
  ok: { bg: "bg-[#E8F0E5]", border: "border-[#CBE0C9]", text: "text-[#4E7150]", icon: Check },
  unknown: { bg: "bg-[#F2EFE9]", border: "border-[#E8E3DA]", text: "text-[#6B6560]", icon: HelpCircle },
};

export default function Scan() {
  const { openVerify } = useOutletContext() || {};
  const [mode, setMode] = useState("symptom");
  const [image, setImage] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [dogId, setDogId] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [breedResult, setBreedResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [upgrade, setUpgrade] = useState(null);
  const resultRef = useRef(null);

  const loadHistory = useCallback(async () => {
    setHistory(await apiFetch("/scan/history").catch(() => []));
  }, []);

  useEffect(() => {
    apiFetch("/dogs").then((d) => {
      setDogs(d || []);
      if (d?.length) setDogId(d[0].id);
    }).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(await fileToBase64(file));
    setResult(null);
    setBreedResult(null);
    e.target.value = "";
  };

  const analyze = async () => {
    if (!image || analyzing) return;
    setAnalyzing(true);
    setResult(null);
    setBreedResult(null);
    try {
      if (mode === "breed") {
        const res = await apiFetch("/breed-identify", {
          method: "POST",
          body: JSON.stringify({ image_base64: image, dog_id: dogId }),
        });
        setBreedResult(res);
      } else {
        const res = await apiFetch("/scan", {
          method: "POST",
          body: JSON.stringify({
            mode,
            image_base64: image,
            dog_id: mode === "symptom" ? dogId : null,
          }),
        });
        setResult(res);
        loadHistory();
      }
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      if (err.status === 402) setUpgrade({ used: err.detail?.used, limit: err.detail?.limit });
      else if (err.status === 403 && err.detail?.code === "email_not_verified") {
        toast.error("Please verify your email to use Snap & Scan.");
        openVerify?.();
      } else if (err.status === 429) toast.error(err.detail?.message || "You're scanning too quickly.");
      else toast.error(err.message || "Scan failed. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const removeScan = async (id) => {
    await apiFetch(`/scan/${id}`, { method: "DELETE" }).catch(() => {});
    setHistory((h) => h.filter((s) => s.id !== id));
  };

  const ModeIcon = MODES[mode].icon;

  return (
    <div className="max-w-3xl mx-auto" data-testid="scan-page">
      <div className="mb-5">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#2D2B2A] flex items-center gap-2">
          <Sparkles size={28} color="#E86A45" /> Snap &amp; Scan
        </h1>
        <p className="text-[#4A4746] mt-1">Instant AI analysis from a single photo.</p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-5">
        {Object.entries(MODES).map(([key, m]) => (
          <button
            key={key}
            data-testid={`scan-mode-${key}`}
            onClick={() => { setMode(key); setResult(null); setBreedResult(null); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-extrabold transition-all border-2 ${
              mode === key ? "bg-[#E86A45] text-white border-[#E86A45]" : "bg-white text-[#4A4746] border-[#E8E3DA] hover:border-[#C4BFB8]"
            }`}
          >
            <m.icon size={18} /> {m.label}
          </button>
        ))}
      </div>

      {/* Capture card */}
      <div className="rounded-3xl border border-[#E8E3DA] bg-white p-5">
        {image ? (
          <div className="relative">
            <img src={image} alt="to scan" className="w-full max-h-80 object-contain rounded-2xl bg-[#F2EFE9]" data-testid="scan-preview" />
            <button
              data-testid="scan-clear-image"
              onClick={() => { setImage(null); setResult(null); }}
              className="absolute top-3 right-3 h-9 w-9 rounded-full bg-[#2D2B2A]/80 text-white flex items-center justify-center"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center py-8">
            <div className="h-16 w-16 rounded-3xl bg-[#F6DED5] flex items-center justify-center mb-3">
              <ModeIcon size={30} color="#E86A45" />
            </div>
            <p className="font-bold text-[#2D2B2A]">{MODES[mode].prompt}</p>
            <div className="flex flex-wrap justify-center gap-3 mt-5">
              <label data-testid="scan-camera-btn" className="cursor-pointer inline-flex items-center gap-2 rounded-full bg-[#E86A45] text-white font-extrabold px-6 py-3 hover:bg-[#d65f3c] transition-colors">
                <Camera size={18} /> Take a photo
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
              </label>
              <label data-testid="scan-upload-btn" className="cursor-pointer inline-flex items-center gap-2 rounded-full bg-[#F2EFE9] text-[#4A4746] font-extrabold px-6 py-3 hover:bg-[#E8E3DA] transition-colors">
                <Upload size={18} /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={pick} />
              </label>
            </div>
          </div>
        )}

        {(mode === "symptom" || mode === "breed") && dogs.length > 0 && image && (
          <div className="relative mt-4 flex items-center gap-2">
            <DogAvatar dog={dogs.find((d) => d.id === dogId)} size={36} />
            <div className="relative flex-1">
              <select
                data-testid="scan-dog-select"
                value={dogId || ""}
                onChange={(e) => setDogId(e.target.value)}
                className="w-full appearance-none text-sm font-bold bg-[#F2EFE9] rounded-full pl-4 pr-9 py-3 outline-none cursor-pointer"
              >
                {dogs.map((d) => <option key={d.id} value={d.id}>For {d.name}</option>)}
              </select>
              <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" color="#4A4746" />
            </div>
          </div>
        )}

        {image && (
          <button
            data-testid="scan-analyze-btn"
            onClick={analyze}
            disabled={analyzing}
            className="w-full mt-4 rounded-full bg-[#E86A45] text-white font-extrabold py-3.5 flex items-center justify-center gap-2 hover:bg-[#d65f3c] disabled:opacity-60 transition-colors"
          >
            {analyzing ? <><Spinner size={20} className="!border-white/40 !border-t-white" /> Analyzing…</> : <><Sparkles size={18} /> Analyze</>}
          </button>
        )}
      </div>

      {/* Result */}
      {result && (
        <div ref={resultRef}>
          <ResultCard result={result} />
        </div>
      )}
      {breedResult && (
        <div ref={resultRef}>
          <BreedResultCard result={breedResult} dogs={dogs} />
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-xl font-extrabold mb-3">Recent scans</h2>
          <div className="space-y-2">
            {history.map((s) => {
              const st = STATUS[s.status] || STATUS.unknown;
              return (
                <div key={s.id} data-testid={`scan-history-${s.id}`} className="flex items-center gap-3 p-3 rounded-2xl bg-white border border-[#E8E3DA]">
                  {s.image_base64 && <img src={s.image_base64.startsWith("data:") ? s.image_base64 : `data:image/jpeg;base64,${s.image_base64}`} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{s.title}</p>
                    <p className={`text-xs font-bold ${st.text}`}>{s.level_label} · {s.mode === "food" ? "Food check" : "Symptom"} · {timeAgo(s.created_at)}</p>
                  </div>
                  <button data-testid={`scan-history-delete-${s.id}`} onClick={() => removeScan(s.id)} className="p-2 rounded-full text-[#B54828] hover:bg-[#F6DED5]">
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <UpgradeModal open={!!upgrade} used={upgrade?.used} limit={upgrade?.limit} onClose={() => setUpgrade(null)} />
    </div>
  );
}

function ResultCard({ result }) {
  const st = STATUS[result.status] || STATUS.unknown;
  const Icon = st.icon;
  return (
    <div className={`mt-5 rounded-3xl border-2 ${st.border} ${st.bg} p-5`} data-testid="scan-result">
      <div className="flex items-center gap-3">
        <div className={`h-11 w-11 rounded-2xl bg-white flex items-center justify-center shrink-0`}>
          <Icon size={22} className={st.text} />
        </div>
        <div className="min-w-0">
          <p className={`text-xs font-extrabold uppercase tracking-wide ${st.text}`} data-testid="scan-result-level">{result.level_label}</p>
          <p className="font-display text-xl font-extrabold text-[#2D2B2A] truncate">{result.title}</p>
        </div>
      </div>

      <p className="text-[#2D2B2A] mt-3 leading-relaxed">{result.summary}</p>

      {result.bullets?.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {result.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#4A4746]">
              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${st.text.replace("text-", "bg-")}`} /> {b}
            </li>
          ))}
        </ul>
      )}

      {result.recommendations?.length > 0 && (
        <div className="mt-4 bg-white/70 rounded-2xl p-4">
          <p className="text-xs font-extrabold uppercase tracking-wide text-[#6B6560] mb-2">What to do</p>
          <ul className="space-y-1.5">
            {result.recommendations.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#2D2B2A]">
                <Check size={15} color="#6F8F72" className="mt-0.5 shrink-0" /> {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(result.see_vet || result.status === "emergency") && (
        <Link
          to="/app/emergency"
          data-testid="scan-emergency-link"
          className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-[#D9534F] text-white font-bold text-sm px-5 py-2.5"
        >
          <Siren size={15} /> Find a 24/7 vet now
        </Link>
      )}

      <p className="text-xs text-[#9A938B] mt-4">AI guidance only — not a substitute for a licensed veterinarian.</p>
    </div>
  );
}
